import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "next-themes";
import { Moon, Sun, Pencil, Trash2, Search, Plus, Upload, User, Users, Settings as SettingsIcon, Camera, BarChart3, MapPin, Bell, AlertTriangle, ShieldAlert, Clock, Info, Building } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useUserManagement, UserWithRole } from "@/hooks/useUserManagement";
import { useTeamStats, TeamMemberStats } from "@/hooks/useTeamStats";
import { useTATAlertRecipients } from "@/hooks/useTATAlertRecipients";
import { useAllUsers } from "@/hooks/useAllUsers";
import { useTATConfigurations } from "@/hooks/useTATConfigurations";
import { useBranchContext } from "@/hooks/useBranchContext";
import { supabase } from "@/integrations/supabase/client";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";
import { OrganizationTab } from "@/components/settings/OrganizationTab";

const allTabs = [
  { id: "account", label: "Account", icon: User, adminOnly: false },
  { id: "team", label: "Team", icon: Users, adminOnly: true },
  { id: "tat", label: "TAT", icon: Clock, adminOnly: true },
  { id: "organization", label: "Organization", icon: Building, adminOnly: true },
];

const Settings = () => {
  const { theme, setTheme } = useTheme();
  const { user, userProfile, userRole, isAdmin, loading: authLoading } = useAuth();
  const { fetchUsers, addUser, updateUser, updateUserRole, deleteUser, updateUserBranches } = useUserManagement();
  const { data: teamStats = [], isLoading: statsLoading, refetch: refetchStats } = useTeamStats();
  const { 
    recipients, 
    isLoading: recipientsLoading, 
    updateLevelRecipients,
    getRecipientsByLevel 
  } = useTATAlertRecipients();
  const { data: allUsers = [], isLoading: allUsersLoading } = useAllUsers();
  const { data: tatConfigurations, isLoading: tatConfigsLoading, updateTATConfiguration } = useTATConfigurations();
  const {
    branches,
    currentBranchId,
    setCurrentBranchId,
    isAdmin: branchIsAdmin,
    userBranchId,
    assignedBranchIds,
    isLoading: branchesLoading,
  } = useBranchContext();

  const availableBranches = branchIsAdmin
    ? branches
    : branches.filter((b) =>
        (assignedBranchIds.length > 0 ? assignedBranchIds : userBranchId != null ? [userBranchId] : []).includes(b.id)
      );
  const isCurrentBranchValid =
    currentBranchId != null && availableBranches.some((b) => b.id === currentBranchId);
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Filter tabs based on admin status
  const tabs = allTabs.filter(tab => !tab.adminOnly || isAdmin);
  const validSettingsTabs = tabs.map(t => t.id);
  
  const tabParam = searchParams.get("tab");
  const activeTab = tabParam && validSettingsTabs.includes(tabParam) ? tabParam : "account";

  const setActiveTab = (tab: string) => {
    setSearchParams({ tab }, { replace: true });
  };
  
  // Redirect to account tab if non-admin tries to access restricted tabs
  useEffect(() => {
    if (!isAdmin && (activeTab === 'team' || activeTab === 'tat')) {
      setActiveTab('account');
    }
  }, [isAdmin, activeTab]);
  
  // Account tab states - derived from userProfile
  const userName = userProfile?.full_name || "Not set";
  const userEmail = userProfile?.email || "Not set";
  const profileBranchId = userBranchId ?? currentBranchId;
  const hospitalBranch =
    branches.find((branch) => branch.id === profileBranchId)?.name ||
    userProfile?.branch ||
    "Not assigned";
  const userDepartment = userProfile?.department || "Not set";
  const userDesignation = userProfile?.designation || "Not set";
  const userPhone = userProfile?.phone_number || user?.phone || "Not set";
  const [profileImage, setProfileImage] = useState("");
  const [isHoveringImage, setIsHoveringImage] = useState(false);

  // Team tab states
  const [teamMembers, setTeamMembers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTeam, setSearchTeam] = useState("");
  const TEAM_FILTER_STORAGE_KEY = "teamBranchFilterMode";
  const [teamBranchFilter, setTeamBranchFilter] = useState<"current" | "all">(() => {
    const stored = localStorage.getItem(TEAM_FILTER_STORAGE_KEY);
    return stored === "all" ? "all" : "current";
  });
  const [selectedMember, setSelectedMember] = useState<UserWithRole | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<UserWithRole | null>(null);
  const [newMember, setNewMember] = useState({
    phone_number: "",
    full_name: "",
    email: "",
    role: "nursing_staff",
    branch_ids: [userBranchId ?? currentBranchId ?? 2].filter((id): id is number => typeof id === "number"),
    department: "",
    designation: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (activeTab === "team") {
      loadUsers();
    }
  }, [activeTab, currentBranchId, teamBranchFilter]);

  const loadUsers = async () => {
    setLoading(true);
    const branchIdToUse = teamBranchFilter === "all" && isAdmin ? null : currentBranchId;
    const { data, error } = await fetchUsers(branchIdToUse);
    if (error) {
      toast({
        title: "Error",
        description: "Failed to load team members",
        variant: "destructive",
      });
    } else if (data) {
      setTeamMembers(data);
    }
    await refetchStats(); // Refresh stats
    setLoading(false);
  };


  // TAT Escalation states
  const [level1Authorities, setLevel1Authorities] = useState<string[]>([]);
  const [level2Authorities, setLevel2Authorities] = useState<string[]>([]);
  const [level3Authorities, setLevel3Authorities] = useState<string[]>([]);
  const [editingConfig, setEditingConfig] = useState<Record<string, any>>({});

  // Load recipients from database when data is available
  useEffect(() => {
    if (recipients && !recipientsLoading) {
      setLevel1Authorities(getRecipientsByLevel(1));
      setLevel2Authorities(getRecipientsByLevel(2));
      setLevel3Authorities(getRecipientsByLevel(3));
    }
  }, [recipients, recipientsLoading, getRecipientsByLevel]);

  // Initialize editing config from TAT configurations
  useEffect(() => {
    if (tatConfigurations) {
      const configs: Record<string, any> = {};
      tatConfigurations.forEach(config => {
        if (config.workflow_step) {
          configs[config.workflow_step] = { ...config };
        }
      });
      setEditingConfig(configs);
    }
  }, [tatConfigurations]);

  const workflowStepNames: Record<string, string> = {
    'MD': 'Marked for Discharge',
    'DSS': 'Discharge Summary Saved',
    'DSF': 'Discharge Summary Finalized',
    'CS': 'Charge Sheet',
    'PC_PHARMACY': 'Pharmacy Clearance',
    'BS': 'Billing Started',
    'BA': 'Billing Approved',
    'PC': 'Payment Completed',
    'PD': 'Physical Discharge'
  };

  const handleSaveSettings = () => {
    toast({
      title: "Settings saved",
      description: "Your preferences have been updated successfully.",
    });
  };

  const handleDeleteMember = async (id: string) => {
    if (!confirm("Are you sure you want to deactivate this user?")) return;
    
    setLoading(true);
    const { error } = await deleteUser(id);
    if (error) {
      toast({
        title: "Error",
        description: "Failed to deactivate user",
        variant: "destructive",
      });
    } else {
      toast({
        title: "User deactivated",
        description: "Team member has been deactivated successfully.",
      });
      loadUsers();
    }
    setLoading(false);
  };

  const handleOpenAddDialog = () => {
    setNewMember({
      phone_number: "",
      full_name: "",
      email: "",
      role: "nursing_staff",
      branch_ids: [userBranchId ?? currentBranchId ?? 2].filter((id): id is number => typeof id === "number"),
      department: "",
      designation: "",
    });
    setFormErrors({});
    setIsAddDialogOpen(true);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    return phone.startsWith('+') && phone.length >= 10;
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddMember = async () => {
    const errors: Record<string, string> = {};
    
    if (!newMember.phone_number) {
      errors.phone = "Phone number is required";
    } else if (!validatePhoneNumber(newMember.phone_number)) {
      errors.phone = "Phone must start with + and country code (e.g., +91)";
    }
    
    if (!newMember.full_name) {
      errors.name = "Full name is required";
    }
    
    if (!newMember.role) {
      errors.role = "Role is required";
    }

    if (!newMember.branch_ids || newMember.branch_ids.length === 0) {
      errors.branch = "At least one branch is required";
    }
    
    if (newMember.email && !validateEmail(newMember.email)) {
      errors.email = "Invalid email format";
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive",
      });
      return;
    }
    
    setFormErrors({});
    setLoading(true);
    const { error } = await addUser(newMember);
    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to add user",
        variant: "destructive",
      });
    } else {
      toast({
        title: "User created",
        description: "User can now login with OTP 123456",
      });
      setIsAddDialogOpen(false);
      loadUsers();
    }
    setLoading(false);
  };

  const handleOpenEditDialog = (member: UserWithRole) => {
    const branchIds =
      member.branch_ids && member.branch_ids.length > 0
        ? member.branch_ids
        : (member as any).branch_id != null
          ? [(member as any).branch_id]
          : [];
    setEditingMember({ ...member, branch_ids: branchIds });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingMember) return;

    // Validate email if provided
    if (editingMember.email && !validateEmail(editingMember.email)) {
      toast({
        title: "Validation Error",
        description: "Invalid email format",
        variant: "destructive",
      });
      return;
    }

    const selectedBranchIds = (editingMember as any).branch_ids || [];
    if (selectedBranchIds.length === 0) {
      toast({
        title: "Validation Error",
        description: "Select at least one branch",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    // Update profile including new fields
    const { error: profileError } = await updateUser(editingMember.id, {
      full_name: editingMember.full_name || undefined,
      email: editingMember.email || undefined,
      branch_id: selectedBranchIds[0],
      department: (editingMember as any).department || undefined,
      designation: (editingMember as any).designation || undefined,
      status: editingMember.status || undefined,
    });

    const { error: branchError } = await updateUserBranches(editingMember.id, selectedBranchIds);

    // Update role
    const newRole = editingMember.user_roles[0]?.role;
    if (newRole) {
      const { error: roleError } = await updateUserRole(editingMember.id, newRole);
      if (roleError) {
        toast({
          title: "Error",
          description: "Failed to update user role",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
    }

    if (profileError || branchError) {
      toast({
        title: "Error",
        description: "Failed to update user details",
        variant: "destructive",
      });
    } else {
      toast({
        title: "User updated",
        description: "Team member details have been updated successfully.",
      });
      setIsEditDialogOpen(false);
      loadUsers();
    }
    setLoading(false);
  };

  const handleViewMember = (member: UserWithRole) => {
    setSelectedMember(member);
    setIsDrawerOpen(true);
  };

  const handleImageUpload = () => {
    toast({
      title: "Image upload",
      description: "Profile image upload functionality would go here.",
    });
  };

  const handleTeamBranchFilterChange = (value: "current" | "all") => {
    setTeamBranchFilter(value);
    localStorage.setItem(TEAM_FILTER_STORAGE_KEY, value);
  };

  const filteredTeamMembers = teamMembers.filter(member =>
    (member.full_name?.toLowerCase() || '').includes(searchTeam.toLowerCase()) ||
    (member.email?.toLowerCase() || '').includes(searchTeam.toLowerCase()) ||
    member.phone_number.toLowerCase().includes(searchTeam.toLowerCase())
  );

  const getRoleDisplayName = (role: string | null | undefined) => {
    if (!role) return 'Not assigned';
    const roleMap: Record<string, string> = {
      'admin': 'Administrator',
      'resident_consultant': 'Resident/Consultant',
      'nursing_staff': 'Nursing Staff',
      'billing_staff': 'Billing Staff',
      'pcs_admin': 'PCS Admin',
      'management': 'Management'
    };
    return roleMap[role] || role;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Tab Menu */}
      <div className="bg-card border-b border-border">
        <div className="container mx-auto px-6">
          <div className="flex items-center space-x-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = tab.id === activeTab;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors relative",
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6 max-w-6xl">

        {/* Account Tab */}
        {activeTab === "account" && (
          <div className="space-y-6">
            {/* Profile Header Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-8 items-start">
                  {/* Profile Image */}
                  <div 
                    className="relative flex-shrink-0"
                    onMouseEnter={() => setIsHoveringImage(true)}
                    onMouseLeave={() => setIsHoveringImage(false)}
                  >
                    <Avatar className="h-40 w-40 border-4 border-border">
                      <AvatarImage src={profileImage} />
                      <AvatarFallback className="text-4xl bg-primary/10 text-primary">
                        {userName.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    {isHoveringImage && (
                      <div 
                        className="absolute inset-0 bg-black/60 rounded-full flex flex-col items-center justify-center cursor-pointer transition-all"
                        onClick={handleImageUpload}
                      >
                        <Camera className="h-8 w-8 text-white mb-1" />
                        <span className="text-xs text-white font-medium">Change Photo</span>
                      </div>
                    )}
                  </div>

                  {/* Profile Info */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <h2 className="text-3xl font-bold text-foreground">{userName}</h2>
                      <p className="text-muted-foreground mt-1">{userEmail}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{userRole}</p>
                          <p className="text-xs text-muted-foreground">Role</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-success/10">
                          <SettingsIcon className="h-5 w-5 text-success" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{hospitalBranch}</p>
                          <p className="text-xs text-muted-foreground">Branch</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-accent/10">
                          <MapPin className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{userDepartment}</p>
                          <p className="text-xs text-muted-foreground">Department</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-warning/10">
                          <User className="h-5 w-5 text-warning" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{userDesignation}</p>
                          <p className="text-xs text-muted-foreground">Designation</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Account Information</CardTitle>
                <CardDescription>Your personal account details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                    <Input
                      id="name"
                      value={userName}
                      disabled
                      className="bg-muted/50 border-muted"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userEmail}
                      disabled
                      className="bg-muted/50 border-muted"
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                    <Input
                      id="phone"
                      value={userPhone}
                      disabled
                      className="bg-muted/50 border-muted"
                    />
                  </div>

                  {/* Hospital Branch */}
                  <div className="space-y-2">
                    <Label htmlFor="branch" className="text-sm font-medium">Hospital Branch</Label>
                    <Input
                      id="branch"
                      value={hospitalBranch}
                      disabled
                      className="bg-muted/50 border-muted"
                    />
                  </div>

                  {/* User Role */}
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-sm font-medium">User Role</Label>
                    <Input
                      id="role"
                      value={userRole || "Not set"}
                      disabled
                      className="bg-muted/50 border-muted"
                    />
                  </div>

                  {/* Department */}
                  <div className="space-y-2">
                    <Label htmlFor="department" className="text-sm font-medium">Department</Label>
                    <Input
                      id="department"
                      value={userDepartment}
                      disabled
                      className="bg-muted/50 border-muted"
                    />
                  </div>

                  {/* Designation */}
                  <div className="space-y-2">
                    <Label htmlFor="designation" className="text-sm font-medium">Designation</Label>
                    <Input
                      id="designation"
                      value={userDesignation}
                      disabled
                      className="bg-muted/50 border-muted"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preferences Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Preferences</CardTitle>
                <CardDescription>Customize your experience</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Theme */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-background border">
                        {theme === "dark" ? (
                          <Moon className="h-5 w-5 text-foreground" />
                        ) : (
                          <Sun className="h-5 w-5 text-foreground" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">Appearance</div>
                        <p className="text-xs text-muted-foreground">
                          {theme === "dark" ? "Dark mode is active" : "Light mode is active"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">Light</span>
                      <Switch
                        checked={theme === "dark"}
                        onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
                      />
                      <span className="text-xs text-muted-foreground">Dark</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Team Tab */}
        {activeTab === "team" && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Team Management</CardTitle>
                  <CardDescription>Manage your team members</CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={handleOpenAddDialog} disabled={loading}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Member
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search team members..."
                    value={searchTeam}
                    onChange={(e) => setSearchTeam(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {isAdmin && (
                  <div className="min-w-[200px]">
                    <Select
                      value={teamBranchFilter}
                      onValueChange={(value) => handleTeamBranchFilterChange(value as "current" | "all")}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Branch filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="current">Current branch</SelectItem>
                        <SelectItem value="all">All branches</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Team Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-center">Tasks Completed</TableHead>
                      <TableHead className="text-center">Avg Time</TableHead>
                      <TableHead>Status</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading || statsLoading ? (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8">
                          Loading team members...
                        </TableCell>
                      </TableRow>
                    ) : filteredTeamMembers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-muted-foreground">
                          No team members found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTeamMembers.map((member) => {
                        const stats = teamStats.find(s => s.id === member.id);
                        const memberBranchIds =
                          member.branch_ids && member.branch_ids.length > 0
                            ? member.branch_ids
                            : (member as any).branch_id != null
                              ? [(member as any).branch_id]
                              : [];
                        const memberBranchName =
                          memberBranchIds.length > 0
                            ? memberBranchIds
                                .map((id) => branches.find((b) => b.id === id)?.name)
                                .filter(Boolean)
                                .join(", ")
                            : member.branch || 'N/A';
                        return (
                          <TableRow 
                            key={member.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleViewMember(member)}
                          >
                            <TableCell>
                              <div>
                                <div className="font-medium">{member.full_name || 'N/A'}</div>
                                <div className="text-xs text-muted-foreground">{member.phone_number}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{getRoleDisplayName(member.user_roles[0]?.role || '')}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{memberBranchName}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium text-sm">
                                {stats?.tasksCompleted || 0}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="text-sm font-medium text-foreground">
                                {stats?.avgCompletionTime 
                                  ? `${Math.round(stats.avgCompletionTime)} min` 
                                  : '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={cn(
                                "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border",
                                member.status === 'active' 
                                  ? "bg-success/10 text-success border-success/20"
                                  : "bg-muted text-muted-foreground border-border"
                              )}>
                                {member.status}
                              </span>
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-end gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleOpenEditDialog(member)}
                                    disabled={loading}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteMember(member.id)}
                                    disabled={loading}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TAT Tab */}
        {activeTab === "tat" && (
          <div className="space-y-6">
          {/* Activity TAT Time Configuration Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Activity TAT Time & Threshold Configuration
              </CardTitle>
              <CardDescription>
                Configure WHEN alerts trigger for each workflow activity. Set allowed TAT time and alert thresholds (Level 1, Level 2, Grace Period).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tatConfigsLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading configurations...
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Table Header */}
                  <div className="grid grid-cols-6 gap-4 p-4 bg-muted/50 rounded-lg font-semibold text-sm">
                    <div>Activity</div>
                    <div>Allowed TAT (min)</div>
                    <div>Level 1 (%)</div>
                    <div>Level 2 (%)</div>
                    <div>Grace Period (min)</div>
                    <div>Actions</div>
                  </div>

                  {/* Activity Rows */}
                  {tatConfigurations?.map((config) => {
                    if (!config.workflow_step) return null;
                    
                    const isEditing = editingConfig[config.workflow_step]?.isEditing;
                    const currentConfig = editingConfig[config.workflow_step] || config;

                    return (
                      <div 
                        key={config.id}
                        className="grid grid-cols-6 gap-4 p-4 border rounded-lg hover:bg-muted/30 transition-colors items-center"
                      >
                        {/* Activity Name */}
                        <div className="font-medium">
                          <div className="text-sm">{workflowStepNames[config.workflow_step]}</div>
                          <div className="text-xs text-muted-foreground">{config.workflow_step}</div>
                        </div>

                        {/* Allowed TAT Minutes */}
                        <div>
                          {isEditing ? (
                            <Input
                              type="number"
                              min="1"
                              value={currentConfig.allowed_tat_minutes}
                              onChange={(e) => {
                                setEditingConfig({
                                  ...editingConfig,
                                  [config.workflow_step!]: {
                                    ...currentConfig,
                                    allowed_tat_minutes: parseInt(e.target.value) || 0
                                  }
                                });
                              }}
                              className="h-8"
                            />
                          ) : (
                            <span className="text-sm">{config.allowed_tat_minutes} min</span>
                          )}
                        </div>

                        {/* Level 1 Threshold */}
                        <div>
                          {isEditing ? (
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={currentConfig.level_1_threshold_percentage}
                              onChange={(e) => {
                                setEditingConfig({
                                  ...editingConfig,
                                  [config.workflow_step!]: {
                                    ...currentConfig,
                                    level_1_threshold_percentage: parseInt(e.target.value) || 0
                                  }
                                });
                              }}
                              className="h-8"
                            />
                          ) : (
                            <span className="text-sm">{config.level_1_threshold_percentage}%</span>
                          )}
                        </div>

                        {/* Level 2 Threshold */}
                        <div>
                          {isEditing ? (
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={currentConfig.level_2_threshold_percentage}
                              onChange={(e) => {
                                setEditingConfig({
                                  ...editingConfig,
                                  [config.workflow_step!]: {
                                    ...currentConfig,
                                    level_2_threshold_percentage: parseInt(e.target.value) || 0
                                  }
                                });
                              }}
                              className="h-8"
                            />
                          ) : (
                            <span className="text-sm">{config.level_2_threshold_percentage}%</span>
                          )}
                        </div>

                        {/* Grace Period */}
                        <div>
                          {isEditing ? (
                            <Input
                              type="number"
                              min="0"
                              value={currentConfig.grace_period_minutes}
                              onChange={(e) => {
                                setEditingConfig({
                                  ...editingConfig,
                                  [config.workflow_step!]: {
                                    ...currentConfig,
                                    grace_period_minutes: parseInt(e.target.value) || 0
                                  }
                                });
                              }}
                              className="h-8"
                            />
                          ) : (
                            <span className="text-sm">{config.grace_period_minutes} min</span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                size="sm"
                                onClick={async () => {
                                  await updateTATConfiguration.mutateAsync({
                                    id: config.id,
                                    allowed_tat_minutes: currentConfig.allowed_tat_minutes,
                                    level_1_threshold_percentage: currentConfig.level_1_threshold_percentage,
                                    level_2_threshold_percentage: currentConfig.level_2_threshold_percentage,
                                    grace_period_minutes: currentConfig.grace_period_minutes
                                  });
                                  
                                  setEditingConfig({
                                    ...editingConfig,
                                    [config.workflow_step!]: {
                                      ...currentConfig,
                                      isEditing: false
                                    }
                                  });
                                  
                                  toast({
                                    title: "Success",
                                    description: `TAT configuration for ${workflowStepNames[config.workflow_step!]} updated`
                                  });
                                }}
                                disabled={updateTATConfiguration.isPending}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingConfig({
                                    ...editingConfig,
                                    [config.workflow_step!]: {
                                      ...config,
                                      isEditing: false
                                    }
                                  });
                                }}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingConfig({
                                  ...editingConfig,
                                  [config.workflow_step!]: {
                                    ...config,
                                    isEditing: true
                                  }
                                });
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Information Panel */}
                  <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="space-y-2 text-sm">
                        <p className="font-medium text-foreground">Understanding TAT Configuration:</p>
                        <ul className="space-y-1 text-muted-foreground">
                          <li>• <strong>Allowed TAT</strong>: Maximum time allowed to complete the activity</li>
                          <li>• <strong>Level 1 Threshold</strong>: When to send reminder to assigned staff (% of TAT)</li>
                          <li>• <strong>Level 2 Threshold</strong>: When to escalate to supervisor (% of TAT)</li>
                          <li>• <strong>Grace Period</strong>: Additional time before Level 3 management escalation</li>
                        </ul>
                        <p className="text-xs italic mt-2">
                          Example: If Allowed TAT is 30 min, Level 1 at 80%, Level 2 at 100%:
                          <br />- Level 1 alert sent at 24 minutes (80% of 30)
                          <br />- Level 2 alert sent at 30 minutes (100% of 30)
                          <br />- Level 3 alert sent after grace period expires
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Clarification Banner */}
          <div className="my-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-2 text-sm">
                <p className="font-medium text-foreground">How TAT Alerts Work:</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>
                    <strong>Section 1 (Above):</strong> Defines WHEN alerts trigger for each activity (TAT time, Level 1%, Level 2%, Grace Period)
                  </li>
                  <li>
                    <strong>Section 2 (Below):</strong> Defines WHO receives alerts at each level (Staff → Supervisor → Management)
                  </li>
                </ul>
                <p className="text-xs italic mt-2">
                  Example: If "Discharge Summary Saved" has Level 1 at 80% and you set 30 min TAT, Level 1 recipients get alerted at 24 minutes. Each activity has its own timing.
                </p>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                TAT Alert Recipients Configuration
              </CardTitle>
              <CardDescription>
                Configure WHO receives alerts at each escalation level. Timing thresholds are configured per activity in the section above.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Level 1: Reminder */}
              <div className="p-6 border-2 border-blue-500/20 rounded-lg bg-blue-500/5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Bell className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      Level 1: Staff Reminder
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Send reminder alerts to assigned staff when activity approaches threshold (thresholds configured per activity below)
                    </p>
                  </div>
                </div>

                <div className="space-y-4 pl-11">
                  <div className="space-y-2">
                    <Label>Send Alerts To</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto p-3 border rounded-lg bg-background">
                      {allUsers.map((user) => (
                        <div key={user.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`level1-${user.id}`}
                            checked={level1Authorities.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setLevel1Authorities([...level1Authorities, user.id]);
                              } else {
                                setLevel1Authorities(level1Authorities.filter(id => id !== user.id));
                              }
                            }}
                          />
                          <Label htmlFor={`level1-${user.id}`} className="cursor-pointer font-normal text-sm">
                            {user.fullName} ({user.role || 'No role'})
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {level1Authorities.length > 0 && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <Label className="text-sm font-medium mb-2 block">
                        Current Recipients ({level1Authorities.length})
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {level1Authorities.map(userId => {
                          const user = allUsers.find(u => u.id === userId);
                          return user ? (
                            <span 
                              key={userId} 
                              className="text-xs px-2 py-1 bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded-full"
                            >
                              {user.fullName}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={async () => {
                      await updateLevelRecipients.mutateAsync({ 
                        level: 1, 
                        userIds: level1Authorities 
                      });
                      toast({
                        title: "Success",
                        description: "Level 1 alert recipients saved",
                      });
                    }}
                    disabled={updateLevelRecipients.isPending}
                    className="w-full"
                  >
                    {updateLevelRecipients.isPending ? "Saving..." : "Save Level 1 Recipients"}
                  </Button>
                </div>
              </div>

              {/* Level 2: Supervisor Escalation */}
              <div className="p-6 border-2 border-yellow-500/20 rounded-lg bg-yellow-500/5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-yellow-500/10">
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      Level 2: Supervisor Escalation
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Escalate to immediate supervisor when activity reaches TAT deadline (thresholds configured per activity below)
                    </p>
                  </div>
                </div>

                <div className="space-y-4 pl-11">
                  <div className="space-y-2">
                    <Label>Send Alerts To (Supervisors)</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto p-3 border rounded-lg bg-background">
                      {allUsers.map((user) => (
                        <div key={user.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`level2-${user.id}`}
                            checked={level2Authorities.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setLevel2Authorities([...level2Authorities, user.id]);
                              } else {
                                setLevel2Authorities(level2Authorities.filter(id => id !== user.id));
                              }
                            }}
                          />
                          <Label htmlFor={`level2-${user.id}`} className="cursor-pointer font-normal text-sm">
                            {user.fullName} ({user.role || 'No role'})
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {level2Authorities.length > 0 && (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <Label className="text-sm font-medium mb-2 block">
                        Current Recipients ({level2Authorities.length})
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {level2Authorities.map(userId => {
                          const user = allUsers.find(u => u.id === userId);
                          return user ? (
                            <span 
                              key={userId} 
                              className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 rounded-full"
                            >
                              {user.fullName}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={async () => {
                      await updateLevelRecipients.mutateAsync({ 
                        level: 2, 
                        userIds: level2Authorities 
                      });
                      toast({
                        title: "Success",
                        description: "Level 2 alert recipients saved",
                      });
                    }}
                    disabled={updateLevelRecipients.isPending}
                    className="w-full"
                  >
                    {updateLevelRecipients.isPending ? "Saving..." : "Save Level 2 Recipients"}
                  </Button>
                </div>
              </div>

              {/* Level 3: Management Escalation */}
              <div className="p-6 border-2 border-red-500/20 rounded-lg bg-red-500/5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <ShieldAlert className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      Level 3: Management Escalation
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Escalate to PCS Head/Management after grace period expires (grace period configured per activity below)
                    </p>
                  </div>
                </div>

                <div className="space-y-4 pl-11">
                  <div className="space-y-2">
                    <Label>Send Alerts To (PCS Head / Management)</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto p-3 border rounded-lg bg-background">
                      {allUsers.map((user) => (
                        <div key={user.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`level3-${user.id}`}
                            checked={level3Authorities.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setLevel3Authorities([...level3Authorities, user.id]);
                              } else {
                                setLevel3Authorities(level3Authorities.filter(id => id !== user.id));
                              }
                            }}
                          />
                          <Label htmlFor={`level3-${user.id}`} className="cursor-pointer font-normal text-sm">
                            {user.fullName} ({user.role || 'No role'})
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {level3Authorities.length > 0 && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <Label className="text-sm font-medium mb-2 block">
                        Current Recipients ({level3Authorities.length})
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {level3Authorities.map(userId => {
                          const user = allUsers.find(u => u.id === userId);
                          return user ? (
                            <span 
                              key={userId} 
                              className="text-xs px-2 py-1 bg-red-500/20 text-red-700 dark:text-red-300 rounded-full"
                            >
                              {user.fullName}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={async () => {
                      await updateLevelRecipients.mutateAsync({ 
                        level: 3, 
                        userIds: level3Authorities 
                      });
                      toast({
                        title: "Success",
                        description: "Level 3 alert recipients saved",
                      });
                    }}
                    disabled={updateLevelRecipients.isPending}
                    className="w-full"
                  >
                    {updateLevelRecipients.isPending ? "Saving..." : "Save Level 3 Recipients"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}
      </div>

      {/* User Analysis Drawer */}
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Team Member Analysis</SheetTitle>
            <SheetDescription>
              Performance metrics and task completion details
            </SheetDescription>
          </SheetHeader>
          {selectedMember && (
            <div className="mt-6 space-y-6">
              {/* Member Info */}
              <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="text-xl">
                    {selectedMember.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-foreground">{selectedMember.full_name || 'N/A'}</h3>
                  <p className="text-sm text-muted-foreground">{selectedMember.phone_number}</p>
                  <p className="text-xs text-muted-foreground">{selectedMember.email || 'No email'}</p>
                  <p className="text-xs text-muted-foreground mt-1">{getRoleDisplayName(selectedMember.user_roles[0]?.role || '')}</p>
                </div>
              </div>

              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <User className="h-4 w-4" />
                  User Details
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg bg-background">
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="text-sm font-medium text-foreground mt-1">{selectedMember.phone_number}</p>
                  </div>
                  <div className="p-4 border rounded-lg bg-background">
                    <p className="text-sm text-muted-foreground">Branch</p>
                    <p className="text-sm font-medium text-foreground mt-1">
                      {(
                        (selectedMember.branch_ids && selectedMember.branch_ids.length > 0
                          ? selectedMember.branch_ids
                          : (selectedMember as any).branch_id != null
                            ? [(selectedMember as any).branch_id]
                            : [])
                          .map((id) => branches.find((b) => b.id === id)?.name)
                          .filter(Boolean)
                          .join(", ")
                      ) || selectedMember.branch || 'N/A'}
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg bg-background">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="text-sm font-medium text-foreground mt-1">{selectedMember.status}</p>
                  </div>
                  <div className="p-4 border rounded-lg bg-background">
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="text-xs font-medium text-foreground mt-1">
                      {new Date(selectedMember.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Member Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>Update team member details</DialogDescription>
          </DialogHeader>
          {editingMember && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone Number</Label>
                <Input
                  id="edit-phone"
                  value={editingMember.phone_number}
                  disabled
                  className="bg-muted/50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  value={editingMember.full_name || ''}
                  onChange={(e) => setEditingMember({ ...editingMember, full_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingMember.email || ''}
                  onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role</Label>
                  <Select 
                    value={editingMember.user_roles[0]?.role || ''} 
                    onValueChange={(value) => setEditingMember({ 
                      ...editingMember, 
                      user_roles: [{ role: value }] 
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrator</SelectItem>
                      <SelectItem value="resident_consultant">Resident/Consultant</SelectItem>
                      <SelectItem value="nursing_staff">Nursing Staff</SelectItem>
                      <SelectItem value="billing_staff">Billing Staff</SelectItem>
                      <SelectItem value="pcs_admin">PCS Admin</SelectItem>
                      <SelectItem value="management">Management</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-branch">Branch</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto p-3 border rounded-lg bg-background">
                    {branches.map((branch) => {
                      const selected = ((editingMember as any).branch_ids || []).includes(branch.id);
                      return (
                        <div key={branch.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-branch-${branch.id}`}
                            checked={selected}
                            onCheckedChange={(checked) => {
                              const current = (editingMember as any).branch_ids || [];
                              const next = checked
                                ? [...current, branch.id]
                                : current.filter((id: number) => id !== branch.id);
                              setEditingMember({ ...editingMember, branch_ids: next });
                            }}
                          />
                          <Label htmlFor={`edit-branch-${branch.id}`} className="cursor-pointer font-normal text-sm">
                            {branch.name}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-department">Department</Label>
                  <Select 
                    value={(editingMember as any).department || ''} 
                    onValueChange={(value) => setEditingMember({ ...editingMember, department: value } as any)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Nursing">Nursing</SelectItem>
                      <SelectItem value="Billing">Billing</SelectItem>
                      <SelectItem value="Administration">Administration</SelectItem>
                      <SelectItem value="Medical">Medical</SelectItem>
                      <SelectItem value="Pharmacy">Pharmacy</SelectItem>
                      <SelectItem value="Radiology">Radiology</SelectItem>
                      <SelectItem value="Laboratory">Laboratory</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-designation">Designation</Label>
                  <Input
                    id="edit-designation"
                    value={(editingMember as any).designation || ''}
                    onChange={(e) => setEditingMember({ ...editingMember, designation: e.target.value } as any)}
                    placeholder="e.g., Senior Nurse"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select 
                  value={editingMember.status || 'active'} 
                  onValueChange={(value) => setEditingMember({ ...editingMember, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Set to inactive to disable user access</p>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={loading}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={loading}>
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Add a new member to your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="new-phone">Phone Number *</Label>
              <Input
                id="new-phone"
                value={newMember.phone_number}
                onChange={(e) => {
                  setNewMember({ ...newMember, phone_number: e.target.value });
                  if (formErrors.phone) setFormErrors({ ...formErrors, phone: '' });
                }}
                placeholder="+919876543210"
                className={formErrors.phone ? "border-destructive" : ""}
              />
              {formErrors.phone ? (
                <p className="text-xs text-destructive">{formErrors.phone}</p>
              ) : (
                <p className="text-xs text-muted-foreground">Include country code (e.g., +91)</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-name">Full Name *</Label>
              <Input
                id="new-name"
                value={newMember.full_name}
                onChange={(e) => {
                  setNewMember({ ...newMember, full_name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                }}
                placeholder="Enter full name"
                className={formErrors.name ? "border-destructive" : ""}
              />
              {formErrors.name && <p className="text-xs text-destructive">{formErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-email">Email</Label>
              <Input
                id="new-email"
                type="email"
                value={newMember.email}
                onChange={(e) => {
                  setNewMember({ ...newMember, email: e.target.value });
                  if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
                }}
                placeholder="user@hospital.com"
                className={formErrors.email ? "border-destructive" : ""}
              />
              {formErrors.email && <p className="text-xs text-destructive">{formErrors.email}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-role">Role *</Label>
                <Select 
                  value={newMember.role} 
                  onValueChange={(value) => {
                    setNewMember({ ...newMember, role: value });
                    if (formErrors.role) setFormErrors({ ...formErrors, role: '' });
                  }}
                >
                  <SelectTrigger className={formErrors.role ? "border-destructive" : ""}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="resident_consultant">Resident/Consultant</SelectItem>
                    <SelectItem value="nursing_staff">Nursing Staff</SelectItem>
                    <SelectItem value="billing_staff">Billing Staff</SelectItem>
                    <SelectItem value="pcs_admin">PCS Admin</SelectItem>
                    <SelectItem value="management">Management</SelectItem>
                  </SelectContent>
                </Select>
                {formErrors.role && <p className="text-xs text-destructive">{formErrors.role}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-branch">Branch</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto p-3 border rounded-lg bg-background">
                  {branches.map((branch) => (
                    <div key={branch.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`new-branch-${branch.id}`}
                        checked={newMember.branch_ids.includes(branch.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setNewMember({ ...newMember, branch_ids: [...newMember.branch_ids, branch.id] });
                          } else {
                            setNewMember({ ...newMember, branch_ids: newMember.branch_ids.filter((id) => id !== branch.id) });
                          }
                        }}
                      />
                      <Label htmlFor={`new-branch-${branch.id}`} className="cursor-pointer font-normal text-sm">
                        {branch.name}
                      </Label>
                    </div>
                  ))}
                </div>
                {formErrors.branch && <p className="text-xs text-destructive">{formErrors.branch}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-department">Department</Label>
                <Select 
                  value={newMember.department} 
                  onValueChange={(value) => setNewMember({ ...newMember, department: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nursing">Nursing</SelectItem>
                    <SelectItem value="Billing">Billing</SelectItem>
                    <SelectItem value="Administration">Administration</SelectItem>
                    <SelectItem value="Medical">Medical</SelectItem>
                    <SelectItem value="Pharmacy">Pharmacy</SelectItem>
                    <SelectItem value="Radiology">Radiology</SelectItem>
                    <SelectItem value="Laboratory">Laboratory</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-designation">Designation</Label>
                <Input
                  id="new-designation"
                  value={newMember.designation}
                  onChange={(e) => setNewMember({ ...newMember, designation: e.target.value })}
                  placeholder="e.g., Senior Nurse"
                />
              </div>
            </div>
            <div className="p-3 bg-muted/50 border rounded-lg">
              <p className="text-xs text-muted-foreground">
                * Required fields. User will be able to login with phone number and OTP: <span className="font-mono font-semibold">123456</span>
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleAddMember} disabled={loading}>
                {loading ? "Creating..." : "Create User"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

        {/* Organization Tab */}
        {activeTab === "organization" && (
          <OrganizationTab />
        )}

    </div>
  );
};

export default Settings;
